import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plus, 
  Calendar, 
  CheckCircle, 
  Clock, 
  Users,
  BarChart3,
  Settings,
  Download,
  Filter,
  Upload
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';
import { canManageAbsences, canApproveAbsences } from '../utils/roleUtils';

// Import enhanced components
import EnhancedAbsenceForm from './EnhancedAbsenceForm';
import AbsenceCalendar from './AbsenceCalendar';
import AbsenceApprovalDashboard from './AbsenceApprovalDashboard';
import AbsenceImportUpload from './AbsenceImportUpload';

const AbsenceManagement = ({ userRoles = [], currentUser = null }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [pendingAbsences, setPendingAbsences] = useState([]);
  const [absenceStats, setAbsenceStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState(null);
  const [exportLoading, setExportLoading] = useState({ csv: false, pdf: false });

  // Use centralized role utilities to ensure super_admin is always included
  const canManage = canManageAbsences(userRoles);
  const canApprove = canApproveAbsences(userRoles);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [employeesList, absencesList, stats] = await Promise.all([
        apiService.getEmployees(0, 500),
        apiService.getAbsences(0, 100),
        apiService.makeRequest('/api/absences/stats/enhanced').catch(() => null)
      ]);

      setEmployees(employeesList);
      setAbsences(absencesList);
      setAbsenceStats(stats);

      // Load pending absences if user can approve
      if (canApprove) {
        const pending = await apiService.getPendingAbsences();
        setPendingAbsences(pending);
      }
    } catch (error) {
      console.error('Failed to load absence data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load absence data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAbsence = async (absenceData) => {
    try {
      const result = await apiService.makeRequest('/api/absences/enhanced', {
        method: 'POST',
        body: JSON.stringify(absenceData)
      });

      toast({
        title: 'Success',
        description: result.validation_result?.auto_approve_eligible 
          ? 'Absence request created and automatically approved'
          : 'Absence request submitted for approval'
      });

      setShowCreateForm(false);
      await loadInitialData();
      
      // Refresh notifications after creating absence
      // Trigger a custom event that NotificationBell/NotificationCenter can listen to
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
      
      // Also try to refresh via API directly as fallback
      setTimeout(async () => {
        try {
          await apiService.get('/api/notifications/unread-count');
        } catch (err) {
          console.error('Failed to refresh notification count:', err);
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to create absence:', error);
      toast({
        title: 'Error',
        description: 'Failed to create absence request',
        variant: 'destructive'
      });
    }
  };

  const handleCalendarDateSelect = (dateStr) => {
    // Set selected dates for new absence form
    setSelectedDates({
      start: dateStr,
      end: dateStr,
      employee_id: currentUser?.id
    });
    setActiveTab('request');
    setShowCreateForm(true);
  };

  // Export handlers (Requirement 4.5)
  const handleExportCSV = async () => {
    setExportLoading(prev => ({ ...prev, csv: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/absences/export/csv`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${await apiService.getAccessToken()}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'absence_export.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: 'Export Successful',
        description: 'CSV file has been downloaded'
      });
    } catch (error) {
      console.error('CSV export failed:', error);
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export CSV file',
        variant: 'destructive'
      });
    } finally {
      setExportLoading(prev => ({ ...prev, csv: false }));
    }
  };

  const handleExportPDF = async () => {
    setExportLoading(prev => ({ ...prev, pdf: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/absences/export/pdf?include_summary=true`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${await apiService.getAccessToken()}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'absence_report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: 'Export Successful',
        description: 'PDF report has been downloaded'
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export PDF report',
        variant: 'destructive'
      });
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-2xl font-bold">{absenceStats?.total_requests || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-600">{absenceStats?.pending_requests || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-green-600">{absenceStats?.approved_requests || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Coverage Rate</p>
                <p className="text-2xl font-bold text-blue-600">
                  {absenceStats?.coverage_arranged_percentage || 0}%
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Absence Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {absences.slice(0, 5).map((absence) => (
              <div key={absence.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <p className="font-medium">{absence.employee_name}</p>
                    <p className="text-sm text-gray-600">
                      {absence.leave_type} • {absence.start_date} to {absence.end_date}
                    </p>
                  </div>
                </div>
                <Badge 
                  variant={
                    absence.status === 'approved' ? 'default' :
                    absence.status === 'pending' ? 'secondary' : 'destructive'
                  }
                >
                  {absence.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button
              onClick={() => {
                setActiveTab('request');
                setShowCreateForm(true);
              }}
              className="h-auto p-4 flex flex-col items-center space-y-2"
            >
              <Plus className="w-6 h-6" />
              <span>New Request</span>
            </Button>

            <Button
              variant="outline"
              onClick={() => setActiveTab('calendar')}
              className="h-auto p-4 flex flex-col items-center space-y-2"
            >
              <Calendar className="w-6 h-6" />
              <span>View Calendar</span>
            </Button>

            {canApprove && (
              <Button
                variant="outline"
                onClick={() => setActiveTab('approvals')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
              >
                <CheckCircle className="w-6 h-6" />
                <span>Approvals ({pendingAbsences.length})</span>
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => setActiveTab('reports')}
              className="h-auto p-4 flex flex-col items-center space-y-2"
            >
              <BarChart3 className="w-6 h-6" />
              <span>Reports</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const RequestTab = () => (
    <div className="space-y-6">
      {showCreateForm ? (
        <EnhancedAbsenceForm
          employees={employees}
          currentUser={currentUser}
          userRoles={userRoles}
          initialData={selectedDates ? {
            employee_id: selectedDates.employee_id,
            start_date: selectedDates.start,
            end_date: selectedDates.end
          } : null}
          onSubmit={handleCreateAbsence}
          onCancel={() => {
            setShowCreateForm(false);
            setSelectedDates(null);
          }}
        />
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Absence Requests</CardTitle>
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {absences.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Absence Requests</h3>
                  <p className="text-gray-600 mb-4">
                    Get started by creating your first absence request.
                  </p>
                  <Button onClick={() => setShowCreateForm(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Request
                  </Button>
                </div>
              ) : (
                absences.map((absence) => (
                  <div key={absence.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium">{absence.employee_name}</h4>
                        <Badge variant="outline">{absence.leave_type}</Badge>
                        <Badge 
                          variant={
                            absence.status === 'approved' ? 'default' :
                            absence.status === 'pending' ? 'secondary' : 'destructive'
                          }
                        >
                          {absence.status}
                        </Badge>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(absence.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Dates:</span>
                        <p>{absence.start_date} to {absence.end_date}</p>
                      </div>
                      <div>
                        <span className="font-medium">Duration:</span>
                        <p>{absence.total_hours} hours</p>
                      </div>
                      <div>
                        <span className="font-medium">Purpose:</span>
                        <p>{absence.purpose}</p>
                      </div>
                      <div>
                        <span className="font-medium">Coverage:</span>
                        <p>{absence.coverage_arranged ? 'Arranged' : 'Not Arranged'}</p>
                      </div>
                    </div>
                    
                    {absence.notes && (
                      <div className="mt-2">
                        <span className="font-medium text-sm">Notes:</span>
                        <p className="text-sm text-gray-600">{absence.notes}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const CalendarTab = () => (
    <AbsenceCalendar
      employees={employees}
      onDateSelect={handleCalendarDateSelect}
      selectedDates={selectedDates}
      showConflicts={true}
    />
  );

  const ApprovalsTab = () => (
    canApprove ? (
      <AbsenceApprovalDashboard userRoles={userRoles} />
    ) : (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
          <p className="text-gray-600">
            You don't have permission to approve absence requests.
          </p>
        </CardContent>
      </Card>
    )
  );

  const ReportsTab = () => (
    <Card>
      <CardHeader>
        <CardTitle>Absence Reports & Analytics</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Enhanced Stats */}
          {absenceStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900">Total Hours</h4>
                <p className="text-2xl font-bold text-blue-600">
                  {absenceStats.total_hours_requested}
                </p>
                <p className="text-sm text-blue-700">
                  {absenceStats.total_hours_approved} approved
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900">Auto-Approved</h4>
                <p className="text-2xl font-bold text-green-600">
                  {absenceStats.auto_approved_count}
                </p>
                <p className="text-sm text-green-700">
                  {(() => {
                    const total = Number(absenceStats.total_requests) || 0;
                    return total > 0 
                      ? ((absenceStats.auto_approved_count / total) * 100).toFixed(1) + '% of total'
                      : '0.0% of total';
                  })()}
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-900">Avg Duration</h4>
                <p className="text-2xl font-bold text-purple-600">
                  {absenceStats.average_request_hours}h
                </p>
                <p className="text-sm text-purple-700">per request</p>
              </div>
            </div>
          )}

          {/* Leave Type Breakdown */}
          {absenceStats?.by_leave_type && (
            <div>
              <h4 className="font-medium mb-3">Requests by Leave Type</h4>
              <div className="space-y-2">
                {Object.entries(absenceStats.by_leave_type).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="font-medium">{type}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Export Options */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Export Reports</h4>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportCSV}
                disabled={exportLoading.csv}
              >
                {exportLoading.csv ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportLoading.csv ? 'Exporting...' : 'Export CSV'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportPDF}
                disabled={exportLoading.pdf}
              >
                {exportLoading.pdf ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportLoading.pdf ? 'Exporting...' : 'Export PDF'}
              </Button>
            </div>
          </div>

          {/* Import Option (Admin/HR only) */}
          {canManageAbsences && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Bulk Import</h4>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowImportModal(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Absences
              </Button>
              <p className="text-xs text-gray-500 mt-2">
                Import multiple absence records from CSV or Excel files
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Absence Management</h1>
          <p className="text-gray-600">
            Manage time-off requests, approvals, and scheduling
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="request">Requests</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          {canApprove && (
            <TabsTrigger value="approvals">
              Approvals {pendingAbsences.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {pendingAbsences.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="request">
          <RequestTab />
        </TabsContent>

        <TabsContent value="calendar">
          <CalendarTab />
        </TabsContent>

        {canApprove && (
          <TabsContent value="approvals">
            <ApprovalsTab />
          </TabsContent>
        )}

        <TabsContent value="reports">
          <ReportsTab />
        </TabsContent>
      </Tabs>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <AbsenceImportUpload
              onImportComplete={() => {
                loadInitialData();
              }}
              onClose={() => setShowImportModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AbsenceManagement;